"use client";

import { Loader2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Универсальный dialog для ввода причины (rejection, refund-reason, и т.п.).
 * Заменяет window.prompt() (например, в reviews/list.tsx rejectMutation).
 */
export function RejectReasonDialog({
  open,
  onClose,
  onConfirm,
  title = "Указать причину",
  description,
  placeholder = "Опишите причину…",
  confirmLabel = "Подтвердить",
  minLength = 3,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  title?: string;
  description?: string;
  placeholder?: string;
  confirmLabel?: string;
  minLength?: number;
}) {
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);

  async function handleConfirm() {
    if (reason.trim().length < minLength) return;
    setPending(true);
    try {
      await onConfirm(reason.trim());
      setReason("");
    } finally {
      setPending(false);
    }
  }

  function handleClose() {
    if (pending) return;
    setReason("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          <Label htmlFor="reject-reason" className="text-xs">
            Причина (минимум {minLength} символов)
          </Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            rows={4}
            disabled={pending}
            autoFocus
          />
          <p className="text-[10px] text-muted-foreground">
            {reason.trim().length}/{minLength}+ символов
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={pending}>
            <X className="h-4 w-4" />
            Отмена
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={pending || reason.trim().length < minLength}
            className="gap-1.5"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
